import { LocalizationProvider, PickersDayProps, StaticDatePicker } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { i18WithParams as t } from '@shared/locale'
import { CustomPickersDay } from '@components/multiple_date_picker/custom_picker_day'
import { useBookingContext } from '@providers/booking'
import { buildPetGroups } from '@shared/booking_groups'
import { buildErrorMessage } from '@shared/error'
import { maximumSelectableDate } from '@shared/service_types'
import { getDateRange, isDataInCache } from '@views/dates_selection/helper'
import { DayCalendarSkeleton } from 'components-ui/src/date_picker_skeleton/date_picker_skeleton'
import { format } from 'date-fns'
import { Services, Types } from 'service-api'
import { captureError } from 'service-api/src/shared'
import { MAX_REQUEST_DATES } from './request_dates'

export const DatePicker = ({
  maxDate,
  startDate,
  providerDates,
}: {
  startDate: Date
  maxDate: Date
  providerDates?: string[]
}) => {
  const bookingCtx = useBookingContext()
  const { state } = bookingCtx
  const { accountName: locationName = '' } = useParams()
  const serviceTypeName = state.serviceTypeName
  const isFetching = useRef<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)
  const endDate = maximumSelectableDate(state.startDate!, state)
  const [requestDates, setRequestDates] = useState<Date[]>(() => {
    return getDateRange(state.startDate!, state.startDate!, endDate)
  })

  const searchParams = useMemo(() => {
    return {
      petGroups: buildPetGroups(state, false),
      offersType: 'PRIMARY',
      offerName: state.cart.groups[0].grooms[0][0].offer.name,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cart.groups[0].grooms[0][0].offer.name])

  useEffect(() => {
    if (isFetching.current) {
      return
    }

    if (isDataInCache(requestDates[0], requestDates[1], state)) {
      setIsLoading(false)
      return
    }

    isFetching.current = true
    const req = async () => {
      const params = {
        ...searchParams,
        startDate: format(requestDates[0], 'yyyy-MM-dd'),
        endDate: format(requestDates[1], 'yyyy-MM-dd'),
      } as unknown as Types.TBookingSearchOptions
      try {
        const response = await Services.ClientBookingSearchService.searchByDays(locationName, serviceTypeName, params)
        bookingCtx.dispatch({
          type: 'SET_OFFER_AVAILABILITY',
          payload: {
            availability: response.results[0]?.availabilityGroupsByDays,
            startDate: requestDates[0],
          },
        })
        bookingCtx.dispatch({ type: 'LOAD_OFFERS_SEARCH_RESULTS', payload: { value: response.results || [] } })

        setIsLoading(false)
        isFetching.current = false
      } catch (error) {
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        captureError(error as Error)
        isFetching.current = false
        setIsLoading(false)
      }
    }
    req()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationName, requestDates, serviceTypeName])

  const shouldDisableDate = useCallback(
    (day: Date) => {
      const idx = state.data.offerAvailability?.findIndex(({ id }) => id === day.getTime()) ?? -1
      if (idx < 0) {
        return true
      }
      const t1 = !state.data.offerAvailability?.[idx]?.availability?.availabilities?.[0]?.available
      const t2 = !state.openDays.some((cb) => cb(format(day, 'yyyy-MM-dd')))
      const t3 = providerDates ? !providerDates.includes(format(day, 'yyyy-MM-dd')) : false
      return t1 || t2 || t3
    },
    [providerDates, state.openDays, state.data.offerAvailability]
  )

  const datesSelected = useMemo(() => state.cart.requestDates?.map((date) => date.date) ?? [], [state])

  const renderPickerDay = useCallback(
    (date: Date, _selectedDates: Date[], pickersDayProps: PickersDayProps<Date>) => {
      const idx = datesSelected.findIndex((dc) => dc.getTime() === date.getTime()) ?? -1
      return <CustomPickersDay {...pickersDayProps} selected={idx >= 0} />
    },
    [datesSelected]
  )

  const onChangeDateValue = useCallback(
    (selectedDate: Date | null) => {
      if (!selectedDate || datesSelected.length >= MAX_REQUEST_DATES) {
        enqueueSnackbar(t('requestDates.grooms.maxDays'), { variant: 'error' })
        return
      }
      bookingCtx.dispatch({
        type: 'SET_REQUEST_DATE',
        payload: { groupIdx: 0, value: selectedDate },
      })
    },
    [bookingCtx, datesSelected]
  )

  const handleMonthChange = (date: Date) => {
    const [fromDate, toDate] = getDateRange(date, state.startDate!, endDate)
    setRequestDates([fromDate, toDate])
    if (isDataInCache(fromDate, toDate, state)) {
      return
    }
    setIsLoading(true)
  }

  const monthButtons = isLoading ? { LeftArrowButton: () => <></>, RightArrowButton: () => <></> } : {}

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StaticDatePicker
        displayStaticWrapperAs="desktop"
        showToolbar={false}
        value={datesSelected}
        minDate={startDate}
        maxDate={maxDate}
        loading={isLoading}
        renderLoading={() => <DayCalendarSkeleton />}
        shouldDisableDate={shouldDisableDate}
        onChange={onChangeDateValue}
        renderDay={renderPickerDay}
        renderInput={(_props) => <></>}
        views={['day']}
        onMonthChange={handleMonthChange}
        components={monthButtons}
      />
    </LocalizationProvider>
  )
}
